import React, { FC } from "react";
import { IOutlets } from "@Libs/Transclusion/types/outlets";
import { Card } from "@UI/Components/Card/Card";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { messages } from "./messages";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { Money } from "@Shared/Components/Money/Money";
import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";
import { ResponsiveFixedButton } from "@UI/Components/ResponsiveFixedButton/ResponsiveFixedButton";
import { Box } from "@UI/Components/Box/Box";
import { BankTransferInformations } from "./BankTransferInformations";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { useOrderPreview } from "@modules/Purchasing/hooks/useOrderPreview";

type Props = Omit<
  IOutlets["configure-payment.body"]["props"],
  "paymentMethod" | "basket"
> & { children?: React.ReactNode };

export const PayByBankTransfer: FC<Props> = ({ onSubmit, children }) => {
  const { currentBasket, isSavingBasket, isLoading } = useCurrentBasket();

  const { orderPreview, isLoading: isOrderPreviewLoading } = useOrderPreview({
    basketId: currentBasket?.id,
    items: currentBasket?.items,
    enabled: !(isSavingBasket || isLoading),
  });

  const pay = useDataMutation(
    () =>
      onSubmit?.({
        paymentMethod: PaymentMethodType.BANK_TRANSFER,
        provider: "company_operations",
        isPaymentConfirmed: false,
      }),
    {
      onError: () => {
        console.error("error");
      },
    }
  );

  if (isLoading || isOrderPreviewLoading) {
    return <LoadingScreen />;
  }

  const amountCents = orderPreview?.itemsTotalCents;
  const currency = orderPreview?.itemsTotalCurrency;

  return (
    <>
      <Card
        w="full"
        background="sand.100"
        p="4"
        rounded="md"
        shadow="none"
        mb={{ md: "3" }}
      >
        <BankTransferInformations
          amountCents={amountCents}
          currency={currency}
        />
      </Card>

      <Box mb={5}>{children}</Box>

      <ResponsiveFixedButton
        data-testid="submit-bank-transfer-button"
        isLoading={pay.isLoading}
        h={12}
        w="full"
        colorScheme="seaGreen"
        onClick={() => pay.mutate()}
        isDisabled={pay.isLoading}
      >
        <FormattedMessage
          {...messages.pay}
          values={{
            amount: <Money cents={amountCents} currency={currency} />,
          }}
        />
      </ResponsiveFixedButton>
    </>
  );
};
